import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface QuickAddNoteCardProps {
  targetBoardId: string | null;
  onNoteCreated?: () => void;
}

// Helper to extract first URL from text
const extractFirstUrl = (text: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  const match = text.match(urlRegex);
  return match ? match[0] : null;
};

export function QuickAddNoteCard({ targetBoardId, onNoteCreated }: QuickAddNoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!noteText.trim() || isSaving) return;
    
    setIsSaving(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error('Please sign in to create notes');
        return;
      }

      // Check if the note contains a URL
      const detectedUrl = extractFirstUrl(noteText);
      
      if (detectedUrl) {
        // Fetch link metadata
        try {
          const { data: metadata, error: metaError } = await supabase.functions.invoke('fetch-link-metadata', {
            body: { url: detectedUrl }
          });
          
          if (!metaError && metadata && metadata.title) {
            // Create as a proper link item with fetched metadata
            const { error } = await supabase
              .from('items')
              .insert({
                user_id: session.user.id,
                board_id: targetBoardId,
                item_type: 'link',
                position_x: 0,
                position_y: 0,
                width: 380,
                height: metadata.image ? 280 : 120,
                content: {
                  url: metadata.url || detectedUrl,
                  title: metadata.title,
                  description: metadata.description || '',
                  image: metadata.image || null,
                  siteName: metadata.siteName || '',
                  favicon: metadata.favicon || null,
                  imageAspectRatio: metadata.imageAspectRatio || null,
                  contentType: metadata.contentType || 'website',
                  price: metadata.price || null,
                  screenshot: metadata.screenshot || null,
                }
              });

            if (error) throw error;
            
            setNoteText('');
            setIsEditing(false);
            onNoteCreated?.();
            return;
          }
        } catch (fetchError) {
          console.log('Failed to fetch link metadata, saving as text card:', fetchError);
          // Fall through to save as text card
        }
      }

      // Create as TEXT CARD (not sticky) with NO board assignment
      const { error } = await supabase
        .from('items')
        .insert({
          user_id: session.user.id,
          board_id: targetBoardId, // Can be null - notes don't require a board
          item_type: 'text_card', // Always text_card, not sticky
          position_x: 0,
          position_y: 0,
          width: 280,
          height: 200,
          content: { body: noteText, title: '' }
        });

      if (error) throw error;
      
      setNoteText('');
      setIsEditing(false);
      onNoteCreated?.();
    } catch (err) {
      console.error('Failed to create note:', err);
      toast.error('Failed to create note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNoteText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      // Check if current line starts with "-" to auto-continue bullet point
      const textarea = e.currentTarget;
      const cursorPos = textarea.selectionStart;
      const textBeforeCursor = noteText.slice(0, cursorPos);
      const currentLineStart = textBeforeCursor.lastIndexOf('\n') + 1;
      const currentLine = textBeforeCursor.slice(currentLineStart);
      
      // If current line starts with "- " and has content, add new bullet
      if (currentLine.match(/^-\s+.+/)) {
        e.preventDefault();
        const newText = noteText.slice(0, cursorPos) + '\n- ' + noteText.slice(cursorPos);
        setNoteText(newText);
        // Set cursor position after the new bullet
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = cursorPos + 3;
        }, 0);
      }
      // If current line is just "- " with no content, remove the bullet and exit list
      else if (currentLine === '- ') {
        e.preventDefault();
        const newText = noteText.slice(0, currentLineStart) + noteText.slice(cursorPos);
        setNoteText(newText);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = currentLineStart;
        }, 0);
      }
    }
  };

  // Handle input change - auto-convert "-" at line start to "- "
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newText = e.target.value;
    const textarea = e.target;
    const cursorPos = textarea.selectionStart;
    
    // Check if user just typed "-" at start of a line
    const textBeforeCursor = newText.slice(0, cursorPos);
    const currentLineStart = textBeforeCursor.lastIndexOf('\n') + 1;
    const currentLine = textBeforeCursor.slice(currentLineStart);
    
    // If line is exactly "-" (just typed), convert to "- "
    if (currentLine === '-') {
      newText = newText.slice(0, cursorPos) + ' ' + newText.slice(cursorPos);
      setNoteText(newText);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = cursorPos + 1;
      }, 0);
      return;
    }
    
    setNoteText(newText);
  };

  return (
    <div 
      className={cn(
        'rounded-xl overflow-hidden transition-all duration-200',
        isEditing 
          ? 'border border-primary bg-paper-raised shadow-lg' 
          : 'border-2 border-dashed border-line bg-paper-raised/50 hover:border-ink-3/40 hover:bg-paper-raised cursor-pointer'
      )}
      style={{ aspectRatio: '3/2' }}
    >
      {isEditing ? (
        <div className="p-4 h-full flex flex-col">
          <textarea
            value={noteText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Start typing..."
            autoFocus
            className="flex-1 w-full bg-transparent resize-none outline-none text-ink placeholder:text-ink-3"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm text-ink-3 hover:text-ink transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!noteText.trim() || isSaving}
              className="px-4 py-1.5 text-sm bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Fetching...</span>
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="w-full h-full p-5 text-left flex flex-col items-start justify-start"
        >
          {/* Text at TOP of card */}
          <p className="text-primary text-sm font-semibold uppercase tracking-wide mb-2">
            Add a new note
          </p>
          <p className="text-ink-3 text-sm">
            Start typing here...
          </p>
        </button>
      )}
    </div>
  );
}
